import React, { useEffect, useRef, useState } from "react";
import { TotalSteps } from "@tripbuilder/common/ProgressBar";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import PageHeaderBar from "@/components/common/PageHeaderBar";
import { motion, AnimatePresence } from "framer-motion";

// Import from feature folder using index file
import {
  TripNameStep,
  DepartureLocationStep,
  DepartureDateStep,
  ConfirmationStep,
} from "@/features/tripCreation";
import useTripCreation from "@/stores/new-trip/useNewTrip";
import { getFlagImage } from "@/lib/general";
import { ConfirmButton } from "@/components/common/buttons";

const NewTrip = () => {
  const navigate = useNavigate();
  const headerRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | null>(null);

  // Get state from zustand store
  const { step, errorMessage, prevStep, createTrip, reset } = useTripCreation();

  // Reset the state when component mounts
  useEffect(() => {
    reset();
  }, [reset]);

  // Calculate content height
  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        const headerHeight = headerRef.current.offsetHeight;
        const topPadding = 12;
        setContentHeight(window.innerHeight - headerHeight - topPadding);
      }
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  // Handle trip creation and navigation
  const handleSubmitTrip = async () => {
    const tripId = await createTrip();
    if (tripId) {
      navigate(`/trip-builder/${tripId}`);
    }
  };

  const stepVariants = {
    enter: {
      opacity: 0,
    },
    center: {
      opacity: 1,
    },
    exit: {
      opacity: 0,
    },
  };

  // Render the appropriate step
  const renderStep = () => {
    switch (step) {
      case 1:
        return <TripNameStep getFlagImage={getFlagImage} />;
      case 2:
        return <DepartureLocationStep getFlagImage={getFlagImage} />;
      case 3:
        return <DepartureDateStep />;
      case 4:
        return <ConfirmationStep onSubmit={handleSubmitTrip} />;
      default:
        return null;
    }
  };

  return (
    <section className="w-full h-full">
      <div ref={headerRef}>
        <PageHeaderBar title="Create" />
      </div>
      <div
        className="relative w-full flex justify-center items-center"
        style={{ height: contentHeight ? `${contentHeight}px` : "100%" }}
      >
        {/* {step < 4 && (
          <TotalSteps
            currentStep={step - 1}
            totalSteps={4}
            className="px-4 h-9 w-auto absolute top-[30%] left-1/2 -translate-x-1/2 -translate-y-1/2"
          />
        )} */}
        <div className="w-full max-w-lg flex flex-col min-h-[200px]">
          <div className="flex-grow">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.15 }}
              >
                {renderStep()}
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="flex items-center justify-center mt-6">
            {step > 1 && (
              <ConfirmButton
                onClick={prevStep}
                className="text-neutral-600 hover:text-neutral-800 bg-neutral-100 hover:bg-neutral-200 rounded-full gap-2 transition-colors duration-200"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="font-light">Back</span>
              </ConfirmButton>
            )}
          </div>

          {errorMessage && (
            <p className="text-red-500 text-sm text-center mt-4">
              {errorMessage}
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default NewTrip;
